import React, { useEffect, useRef } from "react";

/**
 * 360° Panorama viewer using Pannellum (loaded via CDN in index.html).
 * Supports custom hotspots — when clicked they call `onHotspotClick(targetView)`.
 */
export default function PanoramaViewer({ imageUrl, isNight, hotspots = [], onHotspotClick }) {
    const containerRef = useRef(null);
    const viewerRef = useRef(null);
    const handlerRef = useRef(onHotspotClick);

    // Keep latest click handler accessible from Pannellum callbacks
    useEffect(() => { handlerRef.current = onHotspotClick; }, [onHotspotClick]);

    useEffect(() => {
        if (!imageUrl) return;
        if (!window.pannellum) {
            const t = setTimeout(() => containerRef.current && rebuild(), 300);
            return () => clearTimeout(t);
        }
        rebuild();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageUrl, JSON.stringify(hotspots)]);

    const rebuild = () => {
        try {
            if (viewerRef.current && viewerRef.current.destroy) {
                viewerRef.current.destroy();
                viewerRef.current = null;
            }
        } catch (_) { /* ignore */ }

        if (!containerRef.current || !window.pannellum) return;

        const hotSpots = (hotspots || []).map((h, idx) => ({
            id: `hs-${idx}-${h.target_view}`,
            yaw: Number(h.yaw) || 0,
            pitch: Number(h.pitch) || 0,
            type: "info",
            text: h.label || "Enter",
            cssClass: "caprio-hotspot",
            createTooltipFunc: (hotSpotDiv, args) => {
                hotSpotDiv.classList.add("caprio-hotspot");
                hotSpotDiv.setAttribute("data-target", h.target_view);
                hotSpotDiv.setAttribute("data-testid", `hotspot-${h.target_view}`);
                hotSpotDiv.innerHTML = `
                    <div class="caprio-hotspot-ring"></div>
                    <div class="caprio-hotspot-core"></div>
                    <div class="caprio-hotspot-label">${args}</div>
                `;
            },
            createTooltipArgs: h.label || "Enter",
            clickHandlerFunc: () => {
                if (handlerRef.current) handlerRef.current(h.target_view, h);
            },
        }));

        viewerRef.current = window.pannellum.viewer(containerRef.current, {
            type: "equirectangular",
            panorama: imageUrl,
            autoLoad: true,
            autoRotate: hotSpots.length ? 0 : -1.2,
            autoRotateInactivityDelay: 1500,
            showControls: false,
            showZoomCtrl: false,
            showFullscreenCtrl: false,
            mouseZoom: true,
            compass: false,
            haov: 360,
            vaov: 180,
            hfov: 90,
            minHfov: 55,   // prevent user zooming past useful sharpness
            maxHfov: 115,
            crossOrigin: "anonymous",
            sceneFadeDuration: 500,
            backgroundColor: [0, 0, 0],
            hotSpots,
        });
    };

    useEffect(() => {
        return () => {
            try {
                if (viewerRef.current && viewerRef.current.destroy) {
                    viewerRef.current.destroy();
                }
            } catch (_) { /* ignore */ }
        };
    }, []);

    return (
        <div
            ref={containerRef}
            data-testid="panorama-viewer"
            className="absolute inset-0 w-full h-full"
            style={{
                filter: isNight ? "brightness(0.95) contrast(1.08) saturate(1.05)" : "brightness(1.02) contrast(1.05)",
                imageRendering: "high-quality",
            }}
        />
    );
}
